// components
import { Popover } from '../Popover';
import { PopoverContextBox } from '../Popover/Popover.styles';
import PopoverActionSet from '../Popover/components/PopoverActionSet/PopoverActionSet';
import PopoverAction from '../Popover/components/PopoverAction/PopoverAction';

// hooks
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';

// types
import { ContextMenuAction, ContextMenuProps } from './ContextMenu.types';

const ContextMenu = ({
  actions,
  children,
  clickEvent,
  id,
  forceOpen,
  anchorPosition,
  onMenuOpen,
  onMenuClose
}: ContextMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const onClickCallbackRef = useRef<(() => void) | null | undefined>(null);

  const handleClose = (
    event: MouseEvent<HTMLButtonElement>,
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  ) => {
    setIsOpen(false);
    onClickCallbackRef.current = onClick?.bind(null, event);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) onClickCallbackRef.current?.();
  }, [isOpen]);

  const renderActionSet = (actions: ContextMenuAction[]) => {
    const anyIcons = actions.some(({ icon }) => !!icon);
    const anyShortcutHints = actions.some(({ shortcutHint }) => !!shortcutHint);
    const anySubActions = actions.some(
      ({ subActions }) => !!subActions && subActions.length > 0
    );

    return actions.map(
      (
        { value, icon, shortcutHint, disabled, onClick, subActions, separated },
        index
      ) => {
        const isBeforeSeparated = !!actions[index + 1]?.separated;

        if (!!onClick) {
          return (
            <PopoverAction
              key={index}
              disabled={disabled}
              value={value}
              icon={icon}
              forceIconSpace={anyIcons}
              shortcutHint={shortcutHint}
              forceShortcutSpace={anyShortcutHints || anySubActions}
              separated={separated}
              roundedBottom={isBeforeSeparated}
              onClick={(event) =>
                handleClose(event, () => {
                  setTimeout(onClick);
                })
              }
            />
          );
        } else if (subActions && subActions.length > 0) {
          return (
            <PopoverActionSet
              key={index}
              value={value}
              icon={icon}
              forceIconSpace={anyIcons}
            >
              {renderActionSet(subActions)}
            </PopoverActionSet>
          );
        } else {
          return <></>;
        }
      }
    );
  };

  const renderActions = useMemo(() => {
    return renderActionSet(actions);
  }, [actions]);

  return (
    <Popover
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      clickEvent={clickEvent}
      id={id.toString()}
      onMount={setIsOpen.bind(null, true)}
      onOpen={onMenuOpen?.bind(null)}
      onClose={onMenuClose?.bind(null)}
      {...(forceOpen && {
        open: true
      })}
      {...(!forceOpen && {
        open: isOpen
      })}
      component={<PopoverContextBox>{renderActions}</PopoverContextBox>}
      anchorPosition={anchorPosition}
    >
      {children}
    </Popover>
  );
};

export default ContextMenu;
